import { api } from "../api";

/* ── Enums (mirror Prisma) ───────────────────────────────────────── */

export type SupportTicketCategory =
  | "GENERAL_INQUIRY"
  | "TECHNICAL_SUPPORT"
  | "DATABASE_ACCESS"
  | "ACCOUNT_ISSUE"
  | "CATALOG_FEEDBACK"
  | "OTHER";

export type SupportTicketStatus =
  | "OPEN"
  | "PENDING_STAFF"
  | "PENDING_PATRON"
  | "RESOLVED"
  | "CLOSED";

export type AssigneeKind = "STAFF" | "USER";

/* ── Types ───────────────────────────────────────────────────────── */

export interface SupportTicketPerson {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface SupportTicketMessage {
  id: string;
  ticketId: string;
  authorUserId: string | null;
  authorStaffId: string | null;
  body: string;
  createdAt: string;
  readByPatronAt: string | null;
  readByStaffAt: string | null;
  authorUser?: SupportTicketPerson | null;
  authorStaff?: SupportTicketPerson | null;
}

export interface SupportTicket {
  id: string;
  tenantId: string;
  ticketNumber: string;
  subject: string;
  category: SupportTicketCategory;
  status: SupportTicketStatus;
  patronUserId: string;
  assigneeStaffId: string | null;
  assigneeUserId: string | null;
  databaseName: string | null;
  lastMessageAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  patron?: SupportTicketPerson;
  assigneeStaff?: SupportTicketPerson | null;
  assigneeUser?: SupportTicketPerson | null;
  _count?: { messages: number };
  messages?: SupportTicketMessage[];
}

export interface SupportTicketListResponse {
  data: SupportTicket[];
  total: number;
  page: number;
  limit: number;
}

export interface CreateSupportTicketInput {
  subject: string;
  category: SupportTicketCategory;
  message: string;
  databaseName?: string;
}

export interface ListSupportTicketsParams {
  status?: SupportTicketStatus;
  category?: SupportTicketCategory;
  assigneeStaffId?: string;
  assigneeUserId?: string;
  /** "true" → only tickets assigned to the current actor */
  mine?: "true";
  /** "true" → only tickets with no assignee */
  unassigned?: "true";
  search?: string;
  page?: number;
  limit?: number;
}

/**
 * Unified representation of a ticket handler. Either a dedicated Staff record
 * or a User with a staff-like membership (TENANT_ADMIN / LIBRARIAN).
 */
export interface AssignableHandler {
  kind: AssigneeKind;
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  jobTitle: { name: string } | null;
}

export interface AssignTicketPayload {
  assigneeKind: AssigneeKind | null;
  assigneeId: string | null;
}

/* ── API client ──────────────────────────────────────────────────── */

export const supportApi = {
  // Patron
  createMine: (data: CreateSupportTicketInput) =>
    api.post<SupportTicket>("/support/tickets", data).then((r) => r.data),

  listMine: (params?: ListSupportTicketsParams) =>
    api.get<SupportTicketListResponse>("/support/tickets/mine", { params }).then((r) => r.data),

  getMine: (id: string) =>
    api.get<SupportTicket>(`/support/tickets/mine/${id}`).then((r) => r.data),

  replyMine: (id: string, body: string) =>
    api.post<SupportTicket>(`/support/tickets/mine/${id}/reply`, { body }).then((r) => r.data),

  // Staff
  list: (params?: ListSupportTicketsParams) =>
    api.get<SupportTicketListResponse>("/support/tickets", { params }).then((r) => r.data),

  getOne: (id: string) => api.get<SupportTicket>(`/support/tickets/${id}`).then((r) => r.data),

  reply: (id: string, body: string) =>
    api.post<SupportTicket>(`/support/tickets/${id}/reply`, { body }).then((r) => r.data),

  assign: (id: string, payload: AssignTicketPayload) =>
    api.patch<SupportTicket>(`/support/tickets/${id}/assign`, payload).then((r) => r.data),

  transition: (id: string, status: SupportTicketStatus) =>
    api.patch<SupportTicket>(`/support/tickets/${id}/status`, { status }).then((r) => r.data),

  assignableHandlers: () =>
    api.get<AssignableHandler[]>("/support/assignable-staff").then((r) => r.data),
};

/* ── Display helpers ─────────────────────────────────────────────── */

export const TICKET_CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  GENERAL_INQUIRY: "General inquiry",
  TECHNICAL_SUPPORT: "Technical support",
  DATABASE_ACCESS: "Database access (ProQuest, JSTOR, etc.)",
  ACCOUNT_ISSUE: "Account issue",
  CATALOG_FEEDBACK: "Catalog feedback",
  OTHER: "Other",
};

export const TICKET_STATUS_LABELS: Record<SupportTicketStatus, string> = {
  OPEN: "Open",
  PENDING_STAFF: "Waiting on library",
  PENDING_PATRON: "Waiting on you",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

export function formatPerson(p?: SupportTicketPerson | null): string {
  if (!p) return "—";
  const name = [p.firstName, p.lastName].filter(Boolean).join(" ");
  return name || p.email;
}

/** Returns the single current assignee, regardless of kind. */
export function getTicketAssignee(
  ticket: Pick<SupportTicket, "assigneeStaff" | "assigneeUser">
): SupportTicketPerson | null {
  return ticket.assigneeStaff ?? ticket.assigneeUser ?? null;
}

/** Returns the current assignee encoded as a polymorphic value for forms. */
export function getTicketAssigneeValue(
  ticket: Pick<SupportTicket, "assigneeStaffId" | "assigneeUserId">
): { kind: AssigneeKind; id: string } | null {
  if (ticket.assigneeStaffId) return { kind: "STAFF", id: ticket.assigneeStaffId };
  if (ticket.assigneeUserId) return { kind: "USER", id: ticket.assigneeUserId };
  return null;
}
